import { panesFor, type Corner, type LayKind, type PaneRect, type WinSize } from '@/components/camera/recordEngine'

/**
 * Record's icons (Brian's record.html v3.2, Oct 29-30 2026): a layout drawn
 * from the same table as the picture (you amber, the photo grey), a window's
 * corner, and the camera wheel's round buttons.
 */

export const LAY_INFO: Record<LayKind, string> = {
  full: 'You fill the frame',
  side: 'You on the left, the photo beside you',
  sidef: 'The photo on the left, you beside it',
  stack: 'The photo above you, like the phone',
  youwin: 'The photo fills the frame; you in a window at the',
  mediawin: 'You fill the frame; the photo in a window at the',
}
export const CORNER_NAME: Record<Corner, string> = { tl: 'top left', tr: 'top right', bl: 'bottom left', br: 'bottom right' }

/** A layout as a small picture: where you (amber) and the photo (grey) sit. */
export function LayIcon({ kind, corner, size, tall }: { kind: LayKind; corner: Corner; size: WinSize; tall: boolean }) {
  const w = tall ? 24 : 40, h = tall ? 40 : 24, P = panesFor(kind, w, h, corner, size)
  const r = (q: PaneRect | null, c: string, key: string) => q
    ? <rect key={key} x={q.x.toFixed(1)} y={q.y.toFixed(1)} width={q.w.toFixed(1)} height={q.h.toFixed(1)} rx="2" fill={c} />
    : null
  const M = '#a6a6ae', Y = '#e99a52'
  return (
    <svg viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      {P.win === 'media' ? [r(P.you, Y, 'y'), r(P.media, M, 'm')] : [r(P.media, M, 'm'), r(P.you, Y, 'y')]}
    </svg>
  )
}

/** A window's corner: the frame faint, the window amber. */
export function CornerIcon({ c }: { c: Corner }) {
  return (
    <svg viewBox="0 0 16 24" aria-hidden="true">
      <rect className="f" x="0" y="0" width="16" height="24" rx="2" />
      <rect className="w" x={c.endsWith('l') ? 1.5 : 8.5} y={c.startsWith('t') ? 1.5 : 14} width="6" height="8.5" rx="1" />
    </svg>
  )
}

/** The wheel's line icons. */
export const IC = {
  read: <svg viewBox="0 0 24 24"><path d="M5 6h14M5 10h14M5 14h10M5 18h7" /></svg>,
  split: <svg viewBox="0 0 24 24"><rect x="4" y="3" width="16" height="18" rx="2.5" /><path d="M4 10.5h16" /><circle cx="12" cy="15.5" r="1.8" /></svg>,
  green: <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2.5" /><circle cx="12" cy="10.5" r="2.4" /><path d="M8 17c.7-2.2 2.1-3.3 4-3.3s3.3 1.1 4 3.3" /></svg>,
  beauty: <svg viewBox="0 0 24 24"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /><path d="M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z" /></svg>,
  mirror: <svg viewBox="0 0 24 24"><path d="M12 3v18" /><path d="M9 7L4 12l5 5" /><path d="M15 7l5 5-5 5" /></svg>,
  full: <svg viewBox="0 0 24 24"><rect x="4" y="3" width="16" height="18" rx="2.5" /><circle cx="12" cy="10" r="2.6" /><path d="M7 19c.9-3 2.6-4.5 5-4.5s4.1 1.5 5 4.5" /></svg>,
  left: <svg viewBox="0 0 24 24"><rect x="4" y="3" width="16" height="18" rx="2.5" /><circle cx="8.5" cy="12" r="2" /><path d="M5 19c.6-2.2 1.8-3.3 3.5-3.3S11.4 16.8 12 19" /></svg>,
  right: <svg viewBox="0 0 24 24"><rect x="4" y="3" width="16" height="18" rx="2.5" /><circle cx="15.5" cy="12" r="2" /><path d="M12 19c.6-2.2 1.8-3.3 3.5-3.3S18.4 16.8 19 19" /></svg>,
  circle: <svg viewBox="0 0 24 24"><rect x="4" y="3" width="16" height="18" rx="2.5" /><circle cx="12" cy="12" r="5.5" /><circle cx="12" cy="10.5" r="1.8" /></svg>,
  cloth: <svg viewBox="0 0 24 24"><path d="M3 4h18" /><path d="M5 4v15c2.3-1.2 4.7-1.2 7 0s4.7 1.2 7 0V4" /><circle cx="12" cy="10" r="2.2" /><path d="M8.6 16c.6-1.8 1.8-2.7 3.4-2.7s2.8.9 3.4 2.7" /></svg>,
}
